import AxiosOICStat from '../AxiosOICStat';


const BASE = "/cif-admin";
const API_BASE = `${window.location.protocol}//${window.location.hostname}${import.meta.env.VITE_API_URL}`;

export type CifSectionKey =
  | "background"
  | "demography"
  | "economy"
  | "geography"
  | "links"
  | "logistics"
  | "partnership"
  | "politics";

export interface CifAdminCountry {
  c_code: number;
  name: string;
  region: string | null;
  has_background: boolean;
  has_geography: boolean;
  has_demography: boolean;
  has_economy: boolean;
  has_politics: boolean;
  has_partnership: boolean;
  has_logistics: boolean;
  has_links: boolean;
  completed: boolean;
}

// Includes the custom fields configured for the section.
export type CifSectionValues = Record<string, string | number | null>;

export type CifAdminSections = { c_code: number } & Partial<Record<CifSectionKey, CifSectionValues | null>>;

export interface CifFieldConfig {
  id: number;
  section_key: string;
  field_name: string;
  display_label: string;
  field_type: string;
  is_public: boolean;
  is_active: boolean;
  display_order: number;
  is_db_field: boolean;
}

export interface CifFieldConfigCreate {
  section_key: string;
  field_name: string;
  display_label: string;
  field_type?: string;
  is_public?: boolean;
  is_active?: boolean;
  display_order?: number;
}

export type CifFieldConfigUpdate = Partial<
  Pick<CifFieldConfig, "display_label" | "field_type" | "is_public" | "is_active" | "display_order">
>;

export interface CifSectionConfig {
  section_key: string;
  display_name: string;
  display_order: number;
  is_public: boolean;
  is_active: boolean;
}

export type CifSectionConfigUpdate = Partial<Omit<CifSectionConfig, "section_key">>;

export async function fetchCifAdminCountries(lang = "en"): Promise<CifAdminCountry[]> {
  const res = await AxiosOICStat.get<CifAdminCountry[]>(`${BASE}/countries`, { params: { lang } });
  return res.data;
}

export async function fetchCifAdminSections(cCode: number, lang = "en"): Promise<CifAdminSections> {
  const res = await AxiosOICStat.get<CifAdminSections>(`${BASE}/${cCode}/sections`, { params: { lang } });
  return res.data;
}

export async function updateCifAdminSection(cCode: number, section: CifSectionKey, data: CifSectionValues, lang = "en") {
  const res = await AxiosOICStat.put(`${BASE}/${cCode}/${section}`, data, { params: { lang } });
  return res.data;
}

export async function uploadFlag(cCode: number, file: File) {
  const formData = new FormData();
  formData.append("file", file);
  const res = await AxiosOICStat.post(`${BASE}/${cCode}/flag`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

export function getFlagDownloadUrl(cCode: number) {
  return `${API_BASE}cif-admin/${cCode}/flag`;
}

export async function downloadFlag(cCode: number) {
  const res = await AxiosOICStat.get<Blob>(`${BASE}/${cCode}/flag`, {
    responseType: "blob",
  });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const link = document.createElement("a");
  link.href = url;
  const disposition: string | undefined = res.headers["content-disposition"];
  const match = disposition && disposition.match(/filename="?(.+?)"?$/);
  link.download = match ? match[1] : `flag-${cCode}.svg`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
export async function updateCountryRegion(cCode: number, region: string) {
  const res = await AxiosOICStat.put<{ c_code: number; name: string; region: string }>(`${BASE}/${cCode}/region`, { region });
  return res.data;
}

export async function uploadAnthem(cCode: number, file: File) {
  const formData = new FormData();
  formData.append("file", file);
  const res = await AxiosOICStat.post(`${BASE}/${cCode}/anthem`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

export async function downloadAnthem(cCode: number) {
  const res = await AxiosOICStat.get<Blob>(`${BASE}/${cCode}/anthem`, {
    responseType: "blob",
  });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const link = document.createElement("a");
  link.href = url;
  link.download = `anthem-${cCode}.mp3`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

// ─── Field & Section Configuration ──────────────────────────────────────────

export async function seedFieldConfigs() {
  const res = await AxiosOICStat.get<{ message: string }>(`${BASE}/field-config/seed`);
  return res.data;
}

export async function fetchFieldConfigs(sectionKey?: string): Promise<CifFieldConfig[]> {
  const params = sectionKey ? { section_key: sectionKey } : {};
  const res = await AxiosOICStat.get<CifFieldConfig[]>(`${BASE}/field-config`, { params });
  return res.data;
}

export async function updateFieldConfig(fieldId: number, data: CifFieldConfigUpdate): Promise<CifFieldConfig> {
  const res = await AxiosOICStat.put<CifFieldConfig>(`${BASE}/field-config/${fieldId}`, data);
  return res.data;
}

export async function batchUpdateFieldConfigs(
  updates: ({ id: number } & Partial<CifFieldConfig>)[]
): Promise<CifFieldConfig[]> {
  const res = await AxiosOICStat.put<CifFieldConfig[]>(`${BASE}/field-config/batch`, updates);
  return res.data;
}

export async function createFieldConfig(data: CifFieldConfigCreate): Promise<CifFieldConfig> {
  const res = await AxiosOICStat.post<CifFieldConfig>(`${BASE}/field-config`, data);
  return res.data;
}

export async function deleteFieldConfig(fieldId: number) {
  const res = await AxiosOICStat.delete<{ message: string }>(`${BASE}/field-config/${fieldId}`);
  return res.data;
}

export async function fetchSectionConfigs(): Promise<CifSectionConfig[]> {
  const res = await AxiosOICStat.get<CifSectionConfig[]>(`${BASE}/section-config`);
  return res.data;
}

export async function updateSectionConfig(sectionKey: string, data: CifSectionConfigUpdate): Promise<CifSectionConfig> {
  const res = await AxiosOICStat.put<CifSectionConfig>(`${BASE}/section-config/${sectionKey}`, data);
  return res.data;
}
